import React, { useEffect } from 'react'
import { LOGGER } from '@cicero/data/utils'
import {
  Button, Card, Checkbox, Form, FormControlLabel, FormRow, FormSection, Grid,
  MenuItem, Paper, TextField, makeStyles, useForm, useObservable, validators,
} from '@cicero/ui'
import {
  AttributeChooser, AttributeOptionChooser, AttributeTable, FormulaCell, FormulaRenderer,
  PageChooser, PageFilters, TemplateChooser, UserChooser, docValidators,
} from 'forms'
import { AttributeDoc, AttributeSchema, PageSchema, useModel } from 'models'
import { PageCard } from 'views/PageCard'

export const DebugPage = () => {
  const { fakeInput } = useStyles()
  const Attributes = useModel(AttributeSchema)
  const attrs: AttributeDoc[] = useObservable(() => Attributes.QUERY$(), [], [])
  const opts = PageSchema.HOOKS.usePageFilterOptionsLookup()
  const $form = useForm({
    onSubmit: (result) => { LOGGER.debug('submitted', result) },
    onValid: (result) => { LOGGER.debug('valid', result) },
    onError: (result) => { LOGGER.debug('error', result) },
    initialValues: () => ({
      name: '',
      email: '',
      description: 'Default Value',
      job: 'Tinker',
      age: 18,
      formula: '=FOO("BAR", BAR(), ADD(1,2,3), BAR("rand"), BAR(FOO("Cheese")))',
      user: null,
      template: null,
      page: null,
      attribute: null,
      attribute_options: [],
      canEdit: true,
      filters: [],
      filterQuery: {},
    }),
    validateKeys: () => ({
      name: [validators.minLength(3)],
      email: [validators.isEmail],
      page: [docValidators.docPersisted],
    }),
  })

  useEffect(() => {
    $form.setPartialValues({ filterQuery: PageSchema.HOOKS.mapFiltersToPageQuery($form.values.filters, opts) })
  }, [$form.values.filters])

  return (
    <>
      <FormSection title="Forms">
        <DebugFormWrapper values={$form.values}>
          <Form onSubmit={$form.handleSubmit} noValidate>
            <TextField label="Name" required variant="outlined" {...$form.props('name')} />
            <TextField label="Email" variant="outlined" {...$form.props('email')} />
            <TextField label="Description" multiline rows={3} rowsMax={5} variant="outlined" {...$form.props('description')} />
            <TextField label="Job" select required variant="outlined" {...$form.props('job')}>
              { ['Tinker', 'Soldier', 'Tailor', 'Spy'].map(val => <MenuItem key={val} value={val}>{val}</MenuItem>) }
            </TextField>
            <TextField label="Age" type="number" variant="outlined" {...$form.props('age')} />
            <FormRow>
              <FormulaCell label="Formula" {...$form.props('formula')} />
              =
              <Card className={fakeInput} variant="outlined">
                <FormulaRenderer value={$form.values.formula} />
              </Card>
            </FormRow>
            <UserChooser label="User" {...$form.props('user')} />
            <TemplateChooser label="Template" {...$form.props('template')} />
            <PageChooser label="Page" {...$form.props('page')} />
            <AttributeChooser label="Attribute" {...$form.props('attribute')} />
            <AttributeOptionChooser label="Attribute Options" attribute={$form.values.attribute} {...$form.props('attribute_options')} />
            <FormControlLabel label="Can Edit Attributes" control={
              <Checkbox name="canEdit" onChange={(e) => $form.setValues({ ...$form.values, canEdit: e.target.checked })} checked={$form.values.canEdit} />
            }/>
            <Paper variant="outlined" square>
              <PageFilters {...$form.props('filters')} />
            </Paper>
            <Button type="submit">Submit</Button>
          </Form>
        </DebugFormWrapper>
      </FormSection>
      <FormSection title="Page Debugger">
        { $form.values.page
          ? <PageCard
            page={$form.values.page as any}
            attributes={$form.values.attribute ? [$form.values.attribute as any] : attrs}
            quickOpen
          />
          : <>None Selected, choose a page above to see its attributes.</>
        }
        <br /><br />
        <AttributeTable attributes={$form.values.attribute ? [$form.values.attribute as any] : attrs} page={$form.values.page} editable={$form.values.canEdit} />
      </FormSection>
    </>
  )
}

const DebugFormWrapper = ({ children, values, extra }: { children: JSX.Element, values: any, extra?: JSX.Element }) => {
  const { formWrapper } = useStyles()
  return <Grid className={formWrapper} container spacing={3}>
    <Grid item xs={8}>{children}</Grid>
    <Grid item xs={4}>
      <pre>{ JSON.stringify(values, null, 2) }</pre>
      {extra}
    </Grid>
  </Grid>
}

const useStyles = makeStyles({
  fakeInput: {
    padding: 16,
    width: '30%',
  },
  formWrapper: {
    padding: 0,
    marginBottom: 16,
  },
})
